import { Injectable, isDevMode } from "@angular/core";
import { FilterService } from "./filter.service";
import { RuleParserService } from "./rule-parser.service";
import { CosmeticsParserService } from "./cosmetics-parser.service";
import { GameItem } from "../models/game-item.models";
import { DisplayOptions, CosmeticOptions } from "../models/filter-models";
import { DisplayType, Rarity } from "../models/enums";
import { Condition, ConditionOperator, Conditions, FilterBlock } from "../models/parsing-models";
import { SimulatorResult } from "../models/simulator-models";
import { SIMULATOR_DEBUG_MODE } from "../const";

@Injectable({
  providedIn: 'root'
})
export class SimulatorService {
  private readonly debugMode: boolean;
  private parsedBlocksCache: { filterText: string; blocks: FilterBlock[] } | null = null;
  private simulateResultsCache = new Map<string, SimulatorResult>();
  private simulateResultsFilterText: string | null = null;

  constructor(
    private filterService: FilterService,
    private parserService: RuleParserService,
    private cosmeticsParserService: CosmeticsParserService
  ) {
    // Enable debug mode in non-production builds
    this.debugMode = SIMULATOR_DEBUG_MODE && isDevMode();
  }

  simulate(item: GameItem, cacheKey?: string, areaLevel?: number | null): SimulatorResult {
    const filterText = this.filterService.filterResultData().filterText;
    this.ensureSimulateCacheForFilter(filterText);

    const itemKey = this.getSimulateCacheKey(item, cacheKey, areaLevel);
    const cached = this.simulateResultsCache.get(itemKey);
    if (cached) {
      return cached;
    }

    const result = this.runSimulation(item, this.getParsedBlocks(filterText), areaLevel);
    this.simulateResultsCache.set(itemKey, result);
    return result;
  }

  private ensureSimulateCacheForFilter(filterText: string): void {
    if (this.simulateResultsFilterText === filterText) {
      return;
    }
    this.simulateResultsFilterText = filterText;
    this.simulateResultsCache.clear();
    this.parsedBlocksCache = null;
  }

  private getParsedBlocks(filterText: string): FilterBlock[] {
    if (this.parsedBlocksCache?.filterText !== filterText) {
      this.parsedBlocksCache = {
        filterText,
        blocks: this.splitFilterIntoRules(filterText).map(rule => this.parserService.parseFilterBlock(rule)),
      };
    }
    return this.parsedBlocksCache.blocks;
  }

  private getSimulateCacheKey(item: GameItem, cacheKey?: string, areaLevel?: number | null): string {
    if (cacheKey !== undefined) {
      return areaLevel != null ? `${cacheKey}-al${areaLevel}` : cacheKey;
    }
    const base = JSON.stringify(item);
    return areaLevel != null ? `${base}-al${areaLevel}` : base;
  }

  private runSimulation(item: GameItem, blocks: FilterBlock[], areaLevel?: number | null): SimulatorResult {
    const hasFreeTextRules = this.filterHasFreeTextRules();

    const result = new SimulatorResult();
    result.item = item;
    result.styledByPartialRules = [];

    try {
      for (const block of blocks) {
        const evaluationResult = this.evaluateAllConditions(block.conditions, item, areaLevel, block.ruleDescription.name);
        if (evaluationResult.matched) {
          const ruleDescription = structuredClone(block.ruleDescription);
          if (!ruleDescription.name && !ruleDescription.id && hasFreeTextRules) {
            ruleDescription.name = 'Free-Text Rule';
          }

          if (this.debugMode) {
            // Log matching rules with detailed condition information
            const ruleName = block.ruleDescription.name || 'Unnamed';
            const ruleType = block.partial ? 'Partial/Continue' : (block.show ? 'Show' : 'Hide');
            console.log(`[Simulator] ✓ Rule "${ruleName}" MATCHED (${ruleType}):`, {
              ruleName,
              ruleType,
              isPartial: block.partial,
              allConditions: evaluationResult.evaluatedConditions,
              fullText: block.fullText
            });
          }

          if (block.partial) {
            // Partial/Continue rule: add to styledByPartialRules and continue
            result.styledByPartialRules!.push(block);
            // Continue to next block
          } else {
            result.shown = block.show;
            result.rule = ruleDescription;
            result.ruleFullText = block.fullText;
            result.matchingBlock = block;
            // Stop processing once we find a non-partial matching rule
            break;
          }
        } else if (this.debugMode) {
          // Log non-matching rules in debug mode
          const ruleName = block.ruleDescription.name || 'Unnamed';
          const conditionKey = evaluationResult.failedConditionKey || 'unknown';
          const operator = evaluationResult.failedCondition?.operator;
          const conditionValue = evaluationResult.failedConditionValue;
          const itemValue = evaluationResult.itemValue;

          console.log(`[Simulator] ✗ Rule "${ruleName}" did not match - Failed condition:`, {
            ruleName,
            conditionKey,
            operator: operator !== undefined ? operator : 'unknown',
            expectedValue: conditionValue,
            actualItemValue: itemValue,
            conditionDetails: evaluationResult.failedCondition,
            passedConditions: evaluationResult.evaluatedConditions
          });
        }
      }
    } catch (error) {
      result.error = error instanceof Error ? error.message : 'Unable to parse item data';
    }

    if (!result.rule) {
      result.shown = true;
      result.noMatch = true;
      result.rule = {
        name: 'Default',
      }
    }

    result.displayOptions = this.buildDisplayOptions(result);

    return result;
  }

  private buildDisplayOptions(result: SimulatorResult): DisplayOptions {
    const displayOptions = new DisplayOptions({ displayType: DisplayType.Custom });
    displayOptions.hidden = !result.shown;
    displayOptions.cosmeticOptions = new CosmeticOptions();

    // Reverse-engineer cosmeticOptions from the parsed actions
    if (result.styledByPartialRules) {
      for (const block of result.styledByPartialRules) {
        Object.assign(displayOptions.cosmeticOptions, this.cosmeticsParserService.parseCosmeticOptionsFromActions(block.actions));
      }
    }

    if (result.matchingBlock) {
      Object.assign(displayOptions.cosmeticOptions, this.cosmeticsParserService.parseCosmeticOptionsFromActions(result.matchingBlock.actions));
    }

    return displayOptions;
  }

  private filterHasFreeTextRules(): boolean {
    const filter = this.filterService.activeFilter().filter;
    return !!(filter.freeRulesTop?.trim() || filter.freeRulesBottom?.trim());
  }

  /**
   * Evaluates all conditions in a FilterBlock against a GameItem.
   * Returns evaluation result with match status and failure details.
   */
  private evaluateAllConditions(conditions: Conditions, item: GameItem, areaLevel?: number | null, ruleName?: string): {
    matched: boolean;
    failedConditionKey?: string;
    failedCondition?: Condition;
    failedConditionValue?: any;
    itemValue?: any;
    evaluatedConditions?: Array<{
      key: string;
      matched: boolean;
      operator: ConditionOperator;
      expectedValue: string;
      actualItemValue: any;
    }>;
  } {
    const evaluatedConditions: Array<{
      key: string;
      matched: boolean;
      operator: ConditionOperator;
      expectedValue: string;
      actualItemValue: any;
    }> = [];

    for (const [key, condition] of Object.entries(conditions)) {
      if (condition === undefined) {
        continue;
      }

      // Handle arrays of conditions (e.g., multiple hasExplicitMod conditions)
      if (Array.isArray(condition)) {
        // For arrays, ALL conditions must match
        for (const cond of condition) {
          const conditionResult = this.evaluateCondition(key, cond, item, areaLevel);

          // Track all evaluated conditions for debugging
          evaluatedConditions.push({
            key: `${key}[${condition.indexOf(cond)}]`,
            matched: conditionResult.matched,
            operator: cond.operator,
            expectedValue: cond.value,
            actualItemValue: conditionResult.itemValue
          });

          if (!conditionResult.matched) {
            return {
              matched: false,
              failedConditionKey: key,
              failedCondition: cond,
              failedConditionValue: cond.value,
              itemValue: conditionResult.itemValue,
              evaluatedConditions
            };
          }
        }
      } else {
        // Single condition
        const conditionResult = this.evaluateCondition(key, condition, item, areaLevel);

        // Track all evaluated conditions for debugging
        evaluatedConditions.push({
          key,
          matched: conditionResult.matched,
          operator: condition.operator,
          expectedValue: condition.value,
          actualItemValue: conditionResult.itemValue
        });

        if (!conditionResult.matched) {
          return {
            matched: false,
            failedConditionKey: key,
            failedCondition: condition,
            failedConditionValue: condition.value,
            itemValue: conditionResult.itemValue,
            evaluatedConditions
          };
        }
      }
    }

    return { matched: true, evaluatedConditions };
  }

  /**
   * Evaluates a single condition against a GameItem.
   * Returns evaluation result with match status and item value for debugging.
   */
  private evaluateCondition(conditionKey: string, condition: Condition, item: GameItem, areaLevel?: number | null): {
    matched: boolean;
    itemValue?: any;
  } {
    // Special handling for hasExplicitMod condition
    if (conditionKey === 'hasExplicitMod') {
      const matched = this.evaluateHasExplicitMod(condition, item);
      return {
        matched,
        itemValue: item.explicitModNames
      };
    }

    let itemValue = this.getItemValue(conditionKey, item, areaLevel);

    // HasInfluence: undefined means the item has no influence, equivalent to "None"
    if (conditionKey === 'hasInfluence' && itemValue === undefined) {
      itemValue = 'None';
    }

    if (conditionKey === 'rarity') {
      const matched = this.compareRarity(itemValue, condition.operator, condition.value);
      return { matched, itemValue };
    }

    if (itemValue === undefined) {
      const conditionBool = this.parseBoolean(condition.value);
      if (conditionBool !== null) {
        // Boolean condition: undefined item value means false
        const matched = conditionBool === false;
        return { matched, itemValue: undefined };
      }

      // Numeric condition: undefined means the item lacks this attribute, treat as 0
      const numValue = parseFloat(condition.value);
      if (!isNaN(numValue)) {
        const matched = this.compareValues(0, condition.operator, condition.value);
        return { matched, itemValue: 0 };
      }

      // String condition: undefined means the property doesn't exist, so condition fails
      return { matched: false, itemValue: undefined };
    }

    const matched = this.compareValues(itemValue, condition.operator, condition.value);
    return { matched, itemValue };
  }

  /**
   * Gets the value from a GameItem for a given condition key.
   */
  private getItemValue(key: string, item: GameItem, areaLevel?: number | null): any {
    if (key === 'areaLevel') {
      return areaLevel ?? undefined;
    }

    // Special case: 'class' condition maps to 'itemClass' property
    if (key === 'class') {
      return item.itemClass;
    }

    // AnyEnchantment in filter rules checks whether the item has any enchantment.
    // The game item parser stores enchantment text in hasEnchantment, not anyEnchantment.
    if (key === 'anyEnchantment') {
      return !!item.hasEnchantment;
    }

    // For all other keys, access the property directly using bracket notation
    return (item as any)[key];
  }

  private compareRarity(itemRarity: Rarity | 'Currency' | undefined, operator: ConditionOperator, conditionValue: string): boolean {
    if (itemRarity === undefined) return false;

    const itemRarityValue = this.getRarityNumericValue(itemRarity);
    const conditionRarityValue = this.getRarityNumericValue(conditionValue as Rarity | 'Currency');

    if (itemRarityValue === null || conditionRarityValue === null) {
      return false;
    }

    switch (operator) {
      case ConditionOperator.Equals:
      case ConditionOperator.Like:
        return itemRarityValue === conditionRarityValue;
      case ConditionOperator.GreaterThan:
        return itemRarityValue >= conditionRarityValue;
      case ConditionOperator.LessThan:
        return itemRarityValue <= conditionRarityValue;
      case ConditionOperator.Between:
        const [min, max] = conditionValue.split(/\s+/).map(v => this.getRarityNumericValue(v as Rarity | 'Currency'));
        return min !== null && max !== null && itemRarityValue >= min && itemRarityValue <= max;
      default:
        return false;
    }
  }

  private getRarityNumericValue(rarity: Rarity | 'Currency' | string | undefined): number | null {
    if (rarity === undefined) return null;

    switch (rarity) {
      case 'Normal':
        return 0;
      case 'Magic':
        return 1;
      case 'Rare':
        return 2;
      case 'Unique':
        return 3;
      default:
        return 0;
    }
  }

  /**
   * Compares an item value against a condition value using the specified operator.
   */
  private compareValues(itemValue: any, operator: ConditionOperator, conditionValue: string): boolean {
    // Handle boolean values (True/False)
    // The condition value might be "True" or "False" as strings, which should be compared as booleans
    if (typeof itemValue === 'boolean') {
      const conditionBool = this.parseBoolean(conditionValue);
      if (conditionBool !== null) {
        // For boolean comparisons, treat Equals and Like the same way
        // (both should do equality check for booleans)
        if (operator === ConditionOperator.Equals || operator === ConditionOperator.Like) {
          return itemValue === conditionBool;
        }
        // For other operators, treat as not-equal
        return itemValue !== conditionBool;
      }
    }

    // Handle numeric values
    if (typeof itemValue === 'number') {
      const numValue = parseFloat(conditionValue);
      if (!isNaN(numValue)) {
        switch (operator) {
          case ConditionOperator.Equals:
            return itemValue === numValue;
          case ConditionOperator.GreaterThan:
            return itemValue >= numValue;
          case ConditionOperator.LessThan:
            return itemValue <= numValue;
          case ConditionOperator.Between:
            const [min, max] = conditionValue.split(/\s+/).map(v => parseFloat(v));
            return !isNaN(min) && !isNaN(max) && itemValue >= min && itemValue <= max;
        }
      }
    }

    // Handle string values (including class, baseType, etc.)
    if (typeof itemValue === 'string') {
      // For string conditions, check if the condition value contains the item value
      // or if they match exactly. Filter syntax often uses quoted strings with spaces.
      const conditionValues = this.parseStringList(conditionValue);

      switch (operator) {
        case ConditionOperator.Equals:
        case ConditionOperator.Like:
          // Check if item value matches any of the condition values
          return conditionValues.some(cv => this.stringMatches(itemValue, cv, operator === ConditionOperator.Like));
        case ConditionOperator.GreaterThan:
        case ConditionOperator.LessThan:
        case ConditionOperator.Between:
          // These operators don't typically apply to strings, but handle gracefully
          return false;
      }
    }

    // Handle enum values (like Rarity)
    if (itemValue && typeof itemValue === 'object' && 'toString' in itemValue) {
      const itemStr = itemValue.toString();
      const conditionValues = this.parseStringList(conditionValue);

      if (operator === ConditionOperator.Equals || operator === ConditionOperator.Like) {
        return conditionValues.some(cv => this.stringMatches(itemStr, cv, operator === ConditionOperator.Like));
      }
    }

    // Default: strict equality
    return itemValue === conditionValue;
  }

  /**
   * Parses a boolean value from a string (True/False).
   */
  private parseBoolean(value: string): boolean | null {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
    return null;
  }

  /**
   * Parses a string list from filter condition values.
   * Handles quoted strings and space-separated values.
   */
  private parseStringList(value: string): string[] {
    const result: string[] = [];
    const trimmed = value.trim();

    // Handle quoted strings
    const quotedMatches = trimmed.match(/"([^"]+)"/g);
    if (quotedMatches) {
      quotedMatches.forEach(match => {
        result.push(match.slice(1, -1)); // Remove quotes
      });
    }

    // Handle unquoted values (split by spaces, but preserve quoted ones)
    const parts = trimmed.split(/\s+/);
    parts.forEach(part => {
      if (!part.startsWith('"') && !part.endsWith('"') && part.length > 0) {
        result.push(part);
      }
    });

    // If no matches found, return the original value as a single item
    if (result.length === 0) {
      result.push(trimmed);
    }

    return result;
  }

  /**
   * Checks if two strings match, handling case-insensitive comparison.
   */
  private stringMatches(itemValue: string, conditionValue: string, like: boolean): boolean {
    if (like) return itemValue.includes(conditionValue);
    return itemValue === conditionValue;
  }

  /**
   * Evaluates HasExplicitMod condition.
   * Format: HasExplicitMod >=1 "Dictator's" "Emperor's" "Flaring"
   *         HasExplicitMod =0 "Arcing" "Blasting"
   *         HasExplicitMod <=1 "BadMod"
   *
   * Note: The rule parser extracts the operator separately, so condition.operator
   * contains the operator (>=, =, <=) and condition.value contains the count and mod names.
   */
  private evaluateHasExplicitMod(condition: Condition, item: GameItem): boolean {
    const explicitModNames = item.explicitModNames || [];

    // Parse the condition value to extract count and mod names
    // The value format is: "1 "Dictator's" "Emperor's"" or "0 "Arcing" "Blasting""
    const value = condition.value.trim();

    // Extract count from the beginning of the value
    // Format: "1 "Dictator's"..." or just mod names (default to 1)
    const countMatch = value.match(/^(\d+)\s+(.+)$/);
    let count: number;
    let modNamesStr: string;

    if (countMatch) {
      count = parseInt(countMatch[1], 10);
      modNamesStr = countMatch[2].trim();
    } else {
      // No explicit count, default to 1 for >=, 0 for =
      if (condition.operator === ConditionOperator.Equals) {
        count = 0;
      } else {
        count = 1;
      }
      modNamesStr = value;
    }

    // Parse mod names from the string (quoted strings)
    const modNames = this.parseStringList(modNamesStr);

    // If no mod names found, return false
    if (modNames.length === 0) {
      return false;
    }

    // Count how many of the specified mod names appear in item.explicitModNames
    const matchCount = modNames.filter(modName =>
      explicitModNames.some(itemModName =>
        itemModName.toLowerCase() === modName.toLowerCase()
      )
    ).length;

    // Evaluate based on operator
    switch (condition.operator) {
      case ConditionOperator.Equals:
        return matchCount === count;
      case ConditionOperator.GreaterThan:
        return matchCount >= count;
      case ConditionOperator.LessThan:
        return matchCount <= count;
      default:
        // Default to >= count
        return matchCount >= count;
    }
  }

  /**
   * Splits filter output into rules; each rule beginning with either Show or Hide;
   * and continues until the next Show or Hide, or the end of the string
   */
  splitFilterIntoRules(filterText: string): string[] {
    if (!filterText) {
      return [];
    }

    const rules: string[] = [];
    // Match lines that start with "Show" or "Hide" at the beginning of a line
    // This regex matches Show or Hide at the start of a line (possibly after whitespace)
    const ruleStartRegex = /(?:^|\r?\n)(\s*(?:Show|Hide))/gm;
    const matchIndices: number[] = [];

    let match;
    while ((match = ruleStartRegex.exec(filterText)) !== null) {
      // Find the position where the line with "Show" or "Hide" starts
      // match.index points to the newline (or start of string if at beginning)
      // If match.index > 0, there's a newline before, so start after it
      // If match.index === 0, we're at the start of the string
      const lineStart = match.index > 0 ? match.index + 1 : match.index;
      matchIndices.push(lineStart);
    }

    if (matchIndices.length === 0) {
      return [];
    }

    // Extract each rule from its start to the next rule's start (or end of string)
    for (let i = 0; i < matchIndices.length; i++) {
      const startIndex = matchIndices[i];
      const endIndex = i < matchIndices.length - 1 ? matchIndices[i + 1] : filterText.length;
      const rule = filterText.substring(startIndex, endIndex).trim();
      if (rule) {
        rules.push(rule);
      }
    }

    return rules;
  }
}

export { SimulatorResult } from '../models/simulator-models';
